import type { IntegrationConfig, OrgIntegration } from '../../../types/adminConsole';

export const INTEGRATION_KEYS = ['slack', 'teams', 'powerbi', 'webhooks', 'sap', 'totvs'] as const;
export type IntegrationKey = (typeof INTEGRATION_KEYS)[number];

export const WEBHOOK_EVENT_OPTIONS = [
  { key: 'andon.disparado', label: 'Andon disparado' },
  { key: 'task.overdue', label: 'Tarefa atrasada' },
  { key: 'execution.created', label: 'Execução criada' },
  { key: 'execution.completed', label: 'Execução concluída' },
  { key: 'execution.cancelled', label: 'Execução cancelada' },
  { key: 'execution.overdue', label: 'Execução em atraso' },
  { key: 'task.completed', label: 'Tarefa concluída' },
  { key: 'task.assigned', label: 'Tarefa atribuída' },
] as const;

const STATIC_INTEGRATIONS: Omit<
  IntegrationConfig,
  'enabled' | 'status' | 'webhook_url' | 'webhook_secret' | 'events' | 'configured_at'
>[] = [
  {
    id: 'slack',
    key: 'slack',
    label: 'Slack',
    description: 'Receba notificações de workflow e tarefas no Slack.',
    category: 'communication',
  },
  {
    id: 'teams',
    key: 'teams',
    label: 'Microsoft Teams',
    description: 'Integração com canais do Microsoft Teams.',
    category: 'communication',
  },
  {
    id: 'powerbi',
    key: 'powerbi',
    label: 'Power BI',
    description: 'Exportar dados de KPIs e métricas para dashboards Power BI.',
    category: 'analytics',
  },
  {
    id: 'webhooks',
    key: 'webhooks',
    label: 'Webhooks',
    description: 'Envie eventos para endpoints externos via HTTP (Make, Zapier, n8n).',
    category: 'webhook',
  },
  {
    id: 'sap',
    key: 'sap',
    label: 'SAP ERP',
    description: 'Sincronização de processos com sistemas SAP.',
    category: 'erp',
  },
  {
    id: 'totvs',
    key: 'totvs',
    label: 'TOTVS',
    description: 'Integração com ERP TOTVS Protheus.',
    category: 'erp',
  },
];

export const CATEGORY_LABELS: Record<string, string> = {
  communication: 'Comunicação',
  analytics: 'Analytics',
  erp: 'ERP / Sistemas',
  webhook: 'Webhooks',
};

export interface IntegrationSetupMeta {
  title: string;
  intro: string;
  urlLabel: string;
  urlPlaceholder: string;
  secretLabel: string;
  secretPlaceholder: string;
  steps: string[];
}

export const INTEGRATION_SETUP: Record<Exclude<IntegrationKey, 'webhooks'>, IntegrationSetupMeta> = {
  slack: {
    title: 'Configuração — Slack',
    intro: 'Conecte um Incoming Webhook do Slack para receber alertas operacionais desta organização.',
    urlLabel: 'Webhook URL do Slack',
    urlPlaceholder: 'https://hooks.slack.com/services/T000/B000/XXXX',
    secretLabel: 'Signing Secret (opcional)',
    secretPlaceholder: 'Para validar payloads recebidos',
    steps: [
      'No Slack, crie um app e habilite Incoming Webhooks.',
      'Copie a URL do webhook e cole abaixo.',
      'Salve para marcar a integração como conectada.',
    ],
  },
  teams: {
    title: 'Configuração — Microsoft Teams',
    intro: 'Use um conector de webhook de canal do Teams para notificações em tempo real.',
    urlLabel: 'Webhook URL do Teams',
    urlPlaceholder: 'https://outlook.office.com/webhook/...',
    secretLabel: 'Token / Secret (opcional)',
    secretPlaceholder: 'Chave de validação do conector',
    steps: [
      'No canal do Teams, adicione o conector "Incoming Webhook".',
      'Cole a URL gerada no campo abaixo.',
      'Ative os eventos desejados no painel de Webhooks, se necessário.',
    ],
  },
  powerbi: {
    title: 'Configuração — Power BI',
    intro: 'Registre credenciais de acesso para exportação de datasets e KPIs.',
    urlLabel: 'URL do workspace / API',
    urlPlaceholder: 'https://api.powerbi.com/v1.0/myorg/groups/...',
    secretLabel: 'Client Secret / API Key',
    secretPlaceholder: 'Chave de serviço ou token de aplicação',
    steps: [
      'Crie um Service Principal ou use um dataset existente.',
      'Informe endpoint e chave para simulação de conexão.',
      'A sincronização automática será habilitada em versões futuras.',
    ],
  },
  sap: {
    title: 'Configuração — SAP ERP',
    intro: 'Cadastre endpoint OData ou API Gateway para integração com processos SAP.',
    urlLabel: 'Host / Endpoint SAP',
    urlPlaceholder: 'https://sap.empresa.com/sap/opu/odata/...',
    secretLabel: 'API Key / Credencial',
    secretPlaceholder: 'Basic auth, OAuth client secret ou API key',
    steps: [
      'Defina o ambiente (DEV/QAS/PRD) nas notas, se aplicável.',
      'Informe host e credencial para validação de tela.',
      'Conectores RFC/IDoc ficam fora do escopo desta fase.',
    ],
  },
  totvs: {
    title: 'Configuração — TOTVS Protheus',
    intro: 'Configure URL REST do Protheus e token para simulação de vínculo ERP.',
    urlLabel: 'Endpoint REST Protheus',
    urlPlaceholder: 'https://protheus.empresa.com.br/rest/...',
    secretLabel: 'Token de API',
    secretPlaceholder: 'Bearer token ou API key',
    steps: [
      'Habilite API REST no ambiente TOTVS.',
      'Cole endpoint e token abaixo.',
      'Salve para exibir status Conectado na organização.',
    ],
  },
};

const EMAIL_LIKE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Valor inicial do campo URL de webhooks — ignora e-mail/autofill e exige HTTPS salvo. */
export function initialWebhookEndpointUrl(stored: string | null | undefined): string {
  const v = (stored ?? '').trim();
  if (!v || EMAIL_LIKE_PATTERN.test(v)) return '';
  if (!/^https:\/\/.+/i.test(v)) return '';
  return v;
}

/** Valor inicial de endpoint para Slack/SAP/etc. (não aceita formato de e-mail). */
export function initialIntegrationEndpointUrl(stored: string | null | undefined): string {
  const v = (stored ?? '').trim();
  if (!v || EMAIL_LIKE_PATTERN.test(v)) return '';
  return v;
}

export function isIntegrationConfigured(row: OrgIntegration | undefined, key: IntegrationKey): boolean {
  if (!row?.enabled) return false;
  const secret = row.webhook_secret?.trim();
  if (key === 'webhooks') return Boolean(initialWebhookEndpointUrl(row.webhook_url));
  return Boolean(initialIntegrationEndpointUrl(row.webhook_url) || secret);
}

export function mergeIntegrationsWithDb(dbRows: OrgIntegration[]): IntegrationConfig[] {
  return STATIC_INTEGRATIONS.map((s) => {
    const row = dbRows.find((r) => r.integration_key === s.key);
    const key = s.key as IntegrationKey;
    const connected = isIntegrationConfigured(row, key);
    return {
      ...s,
      enabled: row?.enabled ?? false,
      status: connected ? 'connected' : 'disconnected',
      webhook_url:
        key === 'webhooks'
          ? initialWebhookEndpointUrl(row?.webhook_url) || null
          : initialIntegrationEndpointUrl(row?.webhook_url) || null,
      webhook_secret: row?.webhook_secret ?? null,
      events: row?.events ?? null,
      config_json: row?.config_json ?? null,
      configured_at: row?.configured_at ?? undefined,
      organization_id: row?.organization_id,
    };
  });
}

export function getCategories(): string[] {
  return Array.from(new Set(STATIC_INTEGRATIONS.map((i) => i.category)));
}
